import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import Breadcrumb from '@/components/base/Breadcrumb';
import ShareModal from '@/components/base/ShareModal';
import EmptyState from '@/components/base/EmptyState';
import { usePageMeta } from '@/hooks/usePageMeta';
import { castMembers } from '@/mocks/castMembers';
import { plotEpisodes } from '@/mocks/plotEpisodes';
import { episodeNo } from '@/utils/format';

export default function CastDetail() {
  const { id } = useParams();
  const [shareOpen, setShareOpen] = useState(false);
  const member = castMembers.find((item) => String(item.id) === id);

  usePageMeta({
    title: member
      ? `${member.name} 饰 ${member.role} - 禁止的爱善良的小姨子演员介绍`
      : '演员详情 - 禁止的爱善良的小姨子专题',
    description: member
      ? `${member.name}在《禁止的爱善良的小姨子》中饰演${member.role}。${member.bio.slice(0, 60)}`
      : '禁止的爱善良的小姨子演员资料详情。',
    keywords: member
      ? `${member.name},${member.role},禁止的爱善良的小姨子演员,演员资料`
      : '禁止的爱善良的小姨子,演员资料',
  });

  if (!member) {
    return (
      <SiteLayout>
        <div className="mx-auto w-full max-w-3xl px-4 py-16 md:px-8">
          <EmptyState
            title="没有找到这位演员"
            description="该演员资料可能尚未收录，返回演员分类页查看完整名单。"
            actionLabel="返回演员名单"
            actionTo="/category/cast"
          />
        </div>
      </SiteLayout>
    );
  }

  const episodes = plotEpisodes.filter((episode) => member.relatedEpisodes.includes(episode.id)).slice(0, 4);

  return (
    <SiteLayout>
      <div className="border-b border-background-200 bg-background-100">
        <div className="mx-auto w-full px-4 py-5 md:px-8">
          <Breadcrumb
            items={[
              { label: '演员', to: '/category/cast' },
              { label: member.name },
            ]}
          />
        </div>
      </div>

      <article className="mx-auto w-full max-w-[1120px] px-4 pb-24 pt-8 md:px-8 md:pt-10 lg:pb-10">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-10">
          <div className="lg:col-span-4">
            <div className="h-[360px] w-full overflow-hidden rounded-lg bg-background-200 sm:h-[460px] lg:h-[440px]">
              <img
                src={member.image}
                alt={`演员${member.name}饰演${member.role}`}
                title={`${member.name} - 禁止的爱善良的小姨子演员`}
                className="h-full w-full object-cover object-top"
              />
            </div>
            <div className="mt-4 flex items-center justify-between rounded-lg border border-background-200 bg-background-100/70 px-4 py-3">
              <div>
                <p className="text-[11px] text-foreground-400">姓名首字母</p>
                <p className="mt-1 font-heading text-lg text-foreground-900">{member.initial}</p>
              </div>
              <button
                type="button"
                onClick={() => setShareOpen(true)}
                className="hidden items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-4 py-2 text-xs text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-600 lg:flex"
              >
                <span className="flex h-4 w-4 items-center justify-center">
                  <i className="ri-share-line" />
                </span>
                分享
              </button>
            </div>
          </div>

          <div className="lg:col-span-8">
            <p className="text-xs tracking-[0.24em] text-primary-600">CAST PROFILE</p>
            <h1 className="mt-3 font-heading text-3xl text-foreground-950 md:text-4xl">{member.name}</h1>
            <p className="mt-3 flex flex-wrap items-center gap-3 text-sm">
              <span className="rounded-sm bg-primary-100 px-2.5 py-1 text-primary-700">饰 {member.role}</span>
              <span className="text-foreground-500">《禁止的爱善良的小姨子》</span>
            </p>
            <p className="mt-5 border-l-2 border-accent-500 pl-4 font-heading text-base leading-relaxed text-foreground-700">
              {member.tagline}
            </p>

            <section className="mt-7">
              <h2 className="flex items-center gap-2 font-heading text-lg text-foreground-900">
                <span className="h-4 w-[3px] rounded-full bg-primary-600" />
                角色介绍 · {member.role}
              </h2>
              <p className="mt-3 text-[15px] leading-[1.95] text-foreground-700">{member.roleIntro}</p>
            </section>

            <section className="mt-7">
              <h2 className="flex items-center gap-2 font-heading text-lg text-foreground-900">
                <span className="h-4 w-[3px] rounded-full bg-primary-600" />
                演员简介
              </h2>
              <p className="mt-3 text-[15px] leading-[1.95] text-foreground-700">{member.bio}</p>
            </section>

            <div className="mt-6 flex flex-wrap gap-2">
              {['剧情资料', '演员访谈', '幕后花絮'].map((tag) => (
                <span
                  key={tag}
                  className="rounded-full border border-background-300 px-3 py-1 text-xs text-foreground-500"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>
        </div>

        <section className="mt-10 border-t border-background-200 pt-8">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <h2 className="font-heading text-lg text-foreground-900">相关剧集</h2>
              <p className="mt-1 text-xs text-foreground-500">这位角色出现的关键分集，按时间顺序排列</p>
            </div>
            <Link
              to="/category/plot"
              className="flex items-center gap-1 whitespace-nowrap text-xs text-foreground-500 transition-colors hover:text-primary-600"
            >
              全部剧情
              <span className="flex h-3.5 w-3.5 items-center justify-center">
                <i className="ri-arrow-right-line" />
              </span>
            </Link>
          </div>

          <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
            {episodes.map((episode) => (
              <Link
                key={episode.id}
                to={`/detail/plot/${episode.id}`}
                className="group flex items-center gap-4 rounded-lg border border-background-200 bg-background-50 p-3 transition-colors hover:border-primary-300"
              >
                <div className="h-[70px] w-[100px] shrink-0 overflow-hidden rounded-md bg-background-200">
                  <img
                    src={episode.image}
                    alt={`${episodeNo(episode.episode)} ${episode.title} 剧照`}
                    title={`${episode.title} - 禁止的爱善良的小姨子剧情`}
                    className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
                <div className="min-w-0">
                  <p className="text-[11px] text-primary-600">{episodeNo(episode.episode)}</p>
                  <h3 className="mt-1 line-clamp-1 font-heading text-sm text-foreground-900 transition-colors group-hover:text-primary-600">
                    {episode.title}
                  </h3>
                  <p className="mt-1 line-clamp-1 text-xs text-foreground-500">{episode.summary}</p>
                </div>
              </Link>
            ))}
          </div>
        </section>
      </article>

      <div className="fixed inset-x-0 bottom-0 z-40 flex items-center gap-3 border-t border-background-200 bg-background-50 px-4 py-3 lg:hidden">
        <button
          type="button"
          onClick={() => setShareOpen(true)}
          className="flex flex-1 items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-600 py-2.5 text-sm text-background-50"
        >
          <span className="flex h-4 w-4 items-center justify-center">
            <i className="ri-share-line" />
          </span>
          分享演员资料
        </button>
        <Link
          to="/category/cast"
          className="flex flex-1 items-center justify-center whitespace-nowrap rounded-md border border-background-300 py-2.5 text-sm text-foreground-700"
        >
          演员名单
        </Link>
      </div>

      <ShareModal open={shareOpen} onClose={() => setShareOpen(false)} title={`${member.name} 饰 ${member.role}`} />
    </SiteLayout>
  );
}